/** Spec §8.2: centred card, no self-registration, clear credential errors. */

import * as React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/misc';
import { AuthError, AuthScreen, authInputClassName } from '@/components/layout/auth-screen';
import { ApiRequestError } from '@/lib/api/client';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

/** Seeded accounts, surfaced so a reviewer can switch roles without reading the docs. */
const DEMO_ACCOUNTS = [
  { email: 'admin@asfalistiko.gr', roleKey: 'enum.role.admin' },
  { email: 'maria@asfalistiko.gr', roleKey: 'enum.role.agent' },
  { email: 'viewer@asfalistiko.gr', roleKey: 'enum.role.viewer' },
] as const;

const DEMO_PASSWORD = 'Demo1234!';

export function LoginPage() {
  const { t } = useI18n();
  const { signIn } = useAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(email.trim(), password, rememberMe);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : t('error.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const useDemoAccount = (demoEmail: string) => {
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    setError(null);
  };

  /*
    Development only — the production build ships to a real office where these
    accounts do not exist, and a login screen listing credentials it cannot honour
    reads as either broken or alarming. `import.meta.env.DEV` is compile-time, so
    the block (and the demo password literal) is not even present in the bundle.
  */
  const demoAccounts = import.meta.env.DEV && (
    <div className="mt-5">
      <p className="mb-2 text-center text-xs font-medium tracking-wide text-white/45 uppercase">
        {t('auth.demoHint')}
      </p>
      <div className="grid gap-1.5">
        {DEMO_ACCOUNTS.map((account) => (
          <button
            key={account.email}
            type="button"
            onClick={() => useDemoAccount(account.email)}
            className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2 text-left text-[13px] text-white/85 transition-colors hover:border-white/25 hover:bg-white/10"
          >
            <span className="truncate font-medium">{account.email}</span>
            <span className="ml-2 shrink-0 text-white/50">{t(account.roleKey)}</span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-white/40">
        {t('auth.password')}: <code className="font-mono text-white/60">{DEMO_PASSWORD}</code>
      </p>
    </div>
  );

  return (
    <AuthScreen footer={demoAccounts}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={Boolean(error)}
            className={authInputClassName}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={Boolean(error)}
            className={authInputClassName}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-white/75">
            <Checkbox
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(Boolean(checked))}
            />
            {t('auth.rememberMe')}
          </label>
          <Link
            to="/forgot-password"
            className="text-[13px] text-white/70 underline-offset-4 hover:text-white hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        {error && <AuthError>{error}</AuthError>}

        <Button type="submit" className="bg-brand-gradient w-full border-0 shadow-lg shadow-[color:var(--brand-blue)]/25 hover:opacity-95" loading={submitting}>
          {t('auth.signIn')}
        </Button>
      </form>
    </AuthScreen>
  );
}
